import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DependentDto } from 'theSource/features/FamilyMembers/types';
import {
  QuestionContainerStyled,
  QuestionTextStyled,
  RadioGroupStyled,
  RequiredQuestionStyled,
  SurveyResponseContainerStyled,
  FamilyMemberContainerStyled,
  MCQuestionContainerDependentStyled,
} from '../../QuestionnaireStyled';
import { UserResponseDto } from '../../types';
import {
  QuestionDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  generateQuestionForm,
  findParentIndex,
  MULTI_CHOICE_SURVEY_TYPE,
  MULTI_SELECT_SURVEY_TYPE,
  HEIGHT_SURVEY_TYPE,
  SUB_ONLY_TYPE,
  generateDependentMultipleQuestions,
  generateDependentIcon,
  SUB_AND_DEP_TYPE,
  TEXT_SURVEY_TYPE,
  parentQuestionAnswered,
} from '../../utils';
import { FamilyMemberReadonly } from 'enrollment/features/Shared';

interface QuestionProps {
  question: QuestionDto;
  responses: UserResponseDto[];
  flattenedData: QuestionDto[];
  questionResponse: CategoryResponseDto | undefined;
  dependents?: DependentDto[];
  disabled?: boolean;
  onChange?: (e: any) => void;
  isPopup?: boolean;
}

export const Question = ({
  question,
  responses,
  flattenedData,
  questionResponse,
  dependents,
  disabled,
  onChange,
  isPopup = false,
}: QuestionProps) => {
  const { t } = useTranslation('pages.questionnaire');
  const valueLabel = t('value');
  const [visible, setVisible] = useState(false);
  const columnStyle =
    Number(question.valid_responses?.length) > 3 ||
    question.valid_responses?.some((response) => {
      return response.value.length > 28;
    }) ||
    ([
      HEIGHT_SURVEY_TYPE,
      MULTI_CHOICE_SURVEY_TYPE,
      MULTI_SELECT_SURVEY_TYPE,
    ].includes(question.question_type) &&
      question.targeted_at_type !== SUB_ONLY_TYPE);

  const displayStyle = (questionType: string) => {
    switch (questionType) {
      case MULTI_SELECT_SURVEY_TYPE:
      case MULTI_CHOICE_SURVEY_TYPE:
      case HEIGHT_SURVEY_TYPE:
        return 'flex';
      default:
        return 'block';
    }
  };

  const restrictWidth = (questionType: string) => {
    return [HEIGHT_SURVEY_TYPE, TEXT_SURVEY_TYPE].includes(questionType);
  };

  const populateFormElements = (
    question: QuestionDto,
    dependents: DependentDto[] | undefined,
    onChange?: (e: any) => void
  ) => {
    const flexMCOptions =
      (question.valid_responses && question.valid_responses.length > 3) ||
      (question.valid_responses &&
        question.valid_responses?.some((response) => {
          return response.value.length > 28;
        }));

    if (
      question.valid_responses &&
      question.question_type !== MULTI_CHOICE_SURVEY_TYPE
    ) {
      if (question.dependents_in_population) {
        // have to map dependents first for MC questions
        return (
          <>
            {question.dependents_in_population.map((depId) => {
              const dependent = dependents?.find((dep) => {
                return dep.id === depId;
              });
              return (
                <>
                  {generateDependentIcon(dependent, question.question_id)}
                  <MCQuestionContainerDependentStyled $isRow={!flexMCOptions}>
                    {question.valid_responses?.map((response) => {
                      return generateDependentMultipleQuestions(
                        question,
                        questionResponse,
                        depId,
                        response,
                        disabled,
                        onChange
                      );
                    })}
                  </MCQuestionContainerDependentStyled>
                </>
              );
            })}
            {question.targeted_at_type === SUB_AND_DEP_TYPE && (
              <>
                {question.dependents_in_population.length > 0 && (
                  <FamilyMemberContainerStyled>
                    <FamilyMemberReadonly
                      isYou={true}
                      key={`you_q${question.question_id}`}
                    />
                  </FamilyMemberContainerStyled>
                )}
                <MCQuestionContainerDependentStyled $isRow={!flexMCOptions}>
                  {question.valid_responses?.map((response) => {
                    return generateDependentMultipleQuestions(
                      question,
                      questionResponse,
                      undefined,
                      response,
                      disabled,
                      onChange
                    );
                  })}
                </MCQuestionContainerDependentStyled>
              </>
            )}
          </>
        );
      } else {
        return question.valid_responses.map((response) =>
          generateQuestionForm(
            question,
            questionResponse,
            response,
            dependents,
            undefined,
            disabled,
            onChange
          )
        );
      }
    } else if (
      question.valid_responses &&
      question.question_type === MULTI_CHOICE_SURVEY_TYPE
    ) {
      if (question.dependents_in_population) {
        // have to map dependents first for MC questions
        return (
          <>
            {question.dependents_in_population.map((depId) => {
              const dependent = dependents?.find((dep) => {
                return dep.id === depId;
              });

              return (
                <>
                  {generateDependentIcon(dependent, question.question_id)}
                  <RadioGroupStyled
                    name={`radio-group-${question.question_id.toString()}${
                      depId ? '-dep' + depId : ''
                    }`}
                    aria-labelledby={`legend_${question.question_id}`}
                  >
                    {question.valid_responses?.map((response) => {
                      return generateDependentMultipleQuestions(
                        question,
                        questionResponse,
                        depId,
                        response,
                        disabled,
                        onChange
                      );
                    })}
                  </RadioGroupStyled>
                </>
              );
            })}

            {question.targeted_at_type === SUB_AND_DEP_TYPE && (
              <>
                {question.dependents_in_population.length > 0 && (
                  <FamilyMemberContainerStyled>
                    <FamilyMemberReadonly
                      isYou={true}
                      key={`you_q${question.question_id}`}
                    />
                  </FamilyMemberContainerStyled>
                )}
                <RadioGroupStyled
                  name={`radio-group-${question.question_id.toString()}`}
                  aria-labelledby={`legend_${question.question_id}`}
                >
                  {question.valid_responses?.map((response) => {
                    return generateDependentMultipleQuestions(
                      question,
                      questionResponse,
                      undefined,
                      response,
                      disabled,
                      onChange
                    );
                  })}
                </RadioGroupStyled>
              </>
            )}
          </>
        );
      } else {
        return (
          <RadioGroupStyled
            name={`radio-group-${question.question_id.toString()}`}
            aria-labelledby={`legend_${question.question_id}`}
          >
            {question.valid_responses.map((response) =>
              generateQuestionForm(
                question,
                questionResponse,
                response,
                dependents,
                undefined,
                disabled,
                onChange
              )
            )}
          </RadioGroupStyled>
        );
      }
    } else {
      return generateQuestionForm(
        question,
        questionResponse,
        undefined,
        dependents,
        valueLabel,
        disabled,
        onChange
      );
    }
  };

  // useEffect to handle hiding and showing of follow up questions
  useEffect(() => {
    if (question.parent_question_id === undefined) {
      setVisible(true);
      return;
    }

    const parentQuestionIndex = findParentIndex(
      question.parent_question_id,
      flattenedData
    );
    if (parentQuestionIndex === -1 || responses.length < 1) {
      return;
    }

    const parentQuestion = flattenedData.find(
      (questionSearch) =>
        questionSearch.question_id === question.parent_question_id
    );

    if (
      // if parent responses include either ID or other criteria
      parentQuestionAnswered(
        parentQuestion,
        question,
        responses[parentQuestionIndex].response
      )
    ) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  // eslint-disable-next-line
  }, [
    question.display_order,
    question.parent_question_id,
    question.trigger_answers,
    responses,
    flattenedData,
  ]);

  if (!visible || !questionResponse) {
    return null;
  }

  return (
    <QuestionContainerStyled
      data-testid="question-body"
      className="question-body"
      id={`question_${question.question_id}`}
    >
      <>
        <QuestionTextStyled
          className="body1"
          id={`legend_${question.question_id}`}
        >
          {question.question_text}
          {!question.is_optional && (
            <RequiredQuestionStyled variant="body1Heading">
              <span aria-hidden="true" role="presentation">
                *
              </span>{' '}
            </RequiredQuestionStyled>
          )}
        </QuestionTextStyled>
        <SurveyResponseContainerStyled
          $displayStyle={displayStyle(question.question_type)}
          $columnStyle={columnStyle}
          $isHeight={restrictWidth(question.question_type)}
          $isPopup={isPopup}
        >
          {populateFormElements(question, dependents, onChange)}
        </SurveyResponseContainerStyled>
      </>
    </QuestionContainerStyled>
  );
};
